<script setup>
import { reactive, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { required, email } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import NextInput from '@/components/Form/NextInput.vue'
import Button from '@/components/Button.vue'
import Select from '@/components/Form/Select.vue'
import { useAlert } from '@/composables/useToast'
import { AVAILABLE_PERMISSIONS } from '@/constants/permissions'

const emit = defineEmits(['close'])

const store = useStore()
const { t } = useI18n()

const form = reactive({
  name: '',
  email: '',
  role: 'agent',
  permissions: [],
})

const rules = computed(() => ({
  name: { required },
  email: { required, email },
  role: { required },
}))

const v$ = useVuelidate(rules, form)
const roleOptions = computed(() => [
  { value: 'administrator', label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') },
  { value: 'agent', label: t('AGENT_MGMT.AGENT_TYPES.AGENT') },
])


const uiFlags = computed(() => store.getters['agents/getUIFlags'])

const submit = async () => {
  v$.value.$touch()
  if (v$.value.$invalid) return
  try {
    await store.dispatch('agents/create', { ...form })
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS'))
    emit('close')
  } catch (error) {
    useAlert(
      error?.response?.status === 422
        ? t('AGENT_MGMT.ADD.API.EXIST_MESSAGE')
        : t('AGENT_MGMT.ADD.API.ERROR_MESSAGE')
    )
  }
}
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="submit">
    <NextInput
      v-model="form.name"
      name="operator_name"
      type="text"
      required
      :label="$t('AGENT_MGMT.ADD.FORM.NAME.LABEL')"
      :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
      :has-error="v$.name.$error"
      @input="v$.name.$touch"
    />

    <div class="space-y-1">
      <label for="operator_role" class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.ADD.FORM.ROLE.LABEL') }}
      </label>
      <Select id="operator_role" v-model="form.role" full-width :options="roleOptions" />
    </div>

    <div v-if="form.role === 'agent'" class="space-y-1">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.PERMISSIONS.LABEL') }}
      </span>
      <p class="text-xs text-n-slate-11">
        {{ $t('AGENT_MGMT.PERMISSIONS.HINT') }}
      </p>
      <label
        v-for="permission in AVAILABLE_PERMISSIONS"
        :key="permission"
        class="flex items-center gap-2 py-1 text-sm text-n-slate-12 cursor-pointer"
      >
        <input
          v-model="form.permissions"
          type="checkbox"
          :value="permission"
          class="w-4 h-4 accent-n-brand"
        />
        {{ $t(`AGENT_MGMT.PERMISSIONS.${permission}`) }}
      </label>
    </div>

    <NextInput
      v-model="form.email"
      name="operator_email"
      type="email"
      required
      :label="$t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL')"
      :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
      :has-error="v$.email.$error"
      @input="v$.email.$touch"
    />

    <p class="text-xs text-n-slate-11">
      {{ $t('AGENT_MGMT.ADD.FORM.INVITE_HINT') }}
    </p>

    <div class="flex justify-end gap-2 pt-2">
      <Button
        faded
        slate
        type="button"
        :label="$t('AGENT_MGMT.ADD.CANCEL')"
        @click="emit('close')"
      />
      <Button
        type="submit"
        :label="$t('AGENT_MGMT.ADD.SUBMIT')"
        :disabled="uiFlags.isCreating"
      />
    </div>
  </form>
</template>
